import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, VOICES, coverUri } from "../../lib/api";
import { initials } from "../../lib/format";
import { useAuth } from "../../auth";
import { can, isSystemAdmin, permissionsOf } from "../../lib/permissions";
import { Spinner, EmptyState, Button, Field, Chip, Sheet } from "../../components/ui";
import { Icon } from "../../components/icons";

const empty = { name: "", email: "", password: "", voice: null, is_singer: true, role_ids: [] };

function RolesPicker({ roles, value, onChange, canGrant }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div data-testid="member-roles-picker">
      <div className="t-section" style={{ marginBottom: 8 }}>Perfis de acesso</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {roles.map((r) => {
          const on = value.includes(r.id);
          const disabled = !canGrant(r);
          return (
            <label key={r.id} className="card" style={{ display: "flex", gap: 10, alignItems: "center", padding: 10, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1 }}>
              <input type="checkbox" checked={on} disabled={disabled} onChange={() => toggle(r.id)} style={{ accentColor: "var(--brand-primary)" }} data-testid={`member-role-${r.key}`} />
              <span style={{ flex: 1 }}>
                <span style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>{r.name}</span>
                {r.system ? <span className="t-hint" style={{ fontSize: 11 }}> · sistema</span> : null}
                <span className="t-hint" style={{ display: "block", fontSize: 12 }}>{r.description}</span>
              </span>
            </label>
          );
        })}
      </div>
      <div className="t-hint" style={{ fontSize: 12, marginTop: 6 }}>As permissões dos vários perfis somam-se. O perfil Administrador só pode ser atribuído por um Administrador.</div>
    </div>
  );
}

// Same messages as the Mobile app; the shared backend sends the email (send_welcome) and reports welcome_email.
function welcomeNotice(w, isNew, email, asked) {
  if (!asked || !w) return isNew ? { ok: true, text: "Membro criado." } : null;
  if (!w.sent) return { ok: false, text: `${isNew ? "Membro criado" : "Palavra-passe alterada"}, mas o email não foi enviado: ${w.error || "erro desconhecido"}` };
  return { ok: true, text: isNew ? `Membro criado. Email de boas-vindas enviado para ${email}.` : `Palavra-passe alterada. Email enviado para ${email}.` };
}

function MemberForm({ editing, roles, onClose, onSaved }) {
  const { user } = useAuth();
  const isNew = !editing.id;
  const manage = can(user, "members.manage");
  const manageRoles = can(user, "roles.manage");
  const self = editing.id === user?.id;
  const [form, setForm] = useState(isNew ? { ...empty, role_ids: roles.filter((r) => r.system_key === "member").map((r) => r.id) } : { name: editing.name || "", email: editing.email || "", password: "", voice: editing.voice || null, is_singer: editing.is_singer !== false, role_ids: editing.role_ids || [] });
  const [error, setError] = useState("");
  const [sendWelcome, setSendWelcome] = useState(true);
  const [preview, setPreview] = useState(null);
  const loadPreview = useMutation({
    mutationFn: () => api("/admin/members/welcome-preview", { method: "POST", body: JSON.stringify({ name: form.name.trim() || "Novo", email: form.email.trim(), password: "", voice: form.voice }) }),
    onSuccess: setPreview,
    onError: (e) => setError(e.message),
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const mine = permissionsOf(user);
  const canGrant = (r) => {
    if (!manageRoles || (self && !isSystemAdmin(user))) return false;
    if (r.system_key === "admin") return isSystemAdmin(user);
    return isSystemAdmin(user) || (r.permissions || []).every((p) => mine.has(p));
  };

  const save = useMutation({
    mutationFn: async () => {
      let id = editing.id;
      let res_ = null;
      if (manage) {
        if ((isNew || form.password) && form.password.length < 8) throw new Error("A palavra-passe deve ter pelo menos 8 caracteres.");
        if (form.is_singer && !form.voice) throw new Error("Escolha o naipe de quem canta no coro.");
        const body = {};
        if (isNew || form.name.trim() !== editing.name) body.name = form.name.trim();
        if (isNew || form.email.trim() !== editing.email) body.email = form.email.trim();
        if (form.voice && form.voice !== editing.voice) body.voice = form.voice;
        if (isNew || form.password) { body.password = form.password; body.send_welcome = sendWelcome; }
        res_ = isNew || Object.keys(body).length ? await api(isNew ? "/admin/members" : `/admin/members/${id}`, { method: isNew ? "POST" : "PATCH", body: JSON.stringify(body) }) : null;
        id = id || res_.id;
        if (isNew || form.is_singer !== (editing.is_singer !== false)) await api(`/admin/members/${id}`, { method: "PATCH", body: JSON.stringify({ is_singer: form.is_singer }) });
      }
      const before = [...(isNew ? [] : editing.role_ids || [])].sort().join();
      if (manageRoles && [...form.role_ids].sort().join() !== before) await api(`/admin/members/${id}/roles`, { method: "PUT", body: JSON.stringify({ role_ids: form.role_ids }) });
      return welcomeNotice(res_?.welcome_email, isNew, form.email.trim(), sendWelcome && (isNew || !!form.password));
    },
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });

  return (
    <Sheet title={isNew ? "Novo membro" : "Editar membro"} onClose={onClose} testid="member-form">
      <Field label="NOME" value={form.name} disabled={!manage} onChange={(e) => set("name", e.target.value)} placeholder="Nome do membro" data-testid="member-name" />
      <Field label="EMAIL" value={form.email} disabled={!manage} onChange={(e) => set("email", e.target.value)} placeholder="nome@exemplo.pt" type="email" data-testid="member-email" />
      {manage ? <Field label={isNew ? "PALAVRA-PASSE INICIAL" : "NOVA PALAVRA-PASSE (opcional)"} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Mínimo 8 caracteres" type="password" data-testid="member-password" /> : null}
      <label className="card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, cursor: manage ? "pointer" : "default" }}>
        <input type="checkbox" checked={form.is_singer} disabled={!manage} onChange={(e) => set("is_singer", e.target.checked)} style={{ accentColor: "var(--brand-primary)" }} data-testid="member-is-singer" />
        <span style={{ flex: 1 }}>
          <span style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>Canta no coro</span>
          <span className="t-hint" style={{ display: "block", fontSize: 12 }}>Pode ser convocado e usa o chat do seu naipe. Independente dos perfis de acesso.</span>
        </span>
      </label>
      <div>
        <div className="t-section" style={{ marginBottom: 8 }}>Naipe</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {VOICES.map((v) => <Chip key={v} label={v} active={form.voice === v} onClick={() => manage && set("voice", v)} data-testid={`member-voice-${v}`} />)}
          {isNew ? <Chip label="SEM NAIPE" active={!form.voice} onClick={() => set("voice", null)} data-testid="member-voice-none" /> : null}
        </div>
      </div>
      {manage && (isNew || form.password) ? (
        <label className="card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, cursor: "pointer" }} data-testid="member-send-welcome">
          <input type="checkbox" checked={sendWelcome} onChange={(e) => setSendWelcome(e.target.checked)} style={{ accentColor: "var(--brand-primary)" }} data-testid="member-send-welcome-input" />
          <span style={{ flex: 1 }}>
            <span style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>{isNew ? "Enviar email de boas-vindas" : "Enviar email com a nova palavra-passe"}</span>
            <span className="t-hint" style={{ display: "block", fontSize: 12 }}>{isNew ? "Boas-vindas ao ULC com os dados de acesso, primeiros passos e um mini-tutorial da app." : "O membro recebe a nova palavra-passe e o aviso de que as sessões anteriores terminaram."}</span>
          </span>
          <Icon name="mail" size={18} color="var(--muted)" />
        </label>
      ) : null}
      {manage && isNew && sendWelcome ? (
        <Button className="auto sm ghost" icon="eye" loading={loadPreview.isPending} onClick={() => { setError(""); loadPreview.mutate(); }} data-testid="member-welcome-preview">PRÉ-VISUALIZAR EMAIL</Button>
      ) : null}
      {preview ? (
        <Sheet title="Pré-visualização do email" onClose={() => setPreview(null)} testid="member-welcome-preview-sheet" wide>
          <div className="t-hint">Para: <b data-testid="member-welcome-preview-to">{form.email.trim() || "nome@exemplo.pt"}</b> · a palavra-passe aparece escondida na pré-visualização e completa no email enviado.</div>
          <div style={{ fontFamily: "var(--f-medium)" }} data-testid="member-welcome-preview-subject">{preview.subject}</div>
          <iframe title="Pré-visualização do email de boas-vindas" sandbox="" srcDoc={preview.html} style={{ width: "100%", height: "60vh", border: "1px solid var(--border)", borderRadius: "var(--r-md)", background: "#fff" }} data-testid="member-welcome-preview-frame" />
          <div style={{ display: "flex", gap: 8 }}>
            <Button className="ghost" onClick={() => setPreview(null)} data-testid="member-welcome-preview-back">VOLTAR AO FORMULÁRIO</Button>
            <Button loading={save.isPending} onClick={() => { setPreview(null); setError(""); save.mutate(); }} data-testid="member-welcome-preview-confirm">CRIAR E ENVIAR</Button>
          </div>
        </Sheet>
      ) : null}
      {manageRoles ? <RolesPicker roles={roles} value={form.role_ids} onChange={(v) => set("role_ids", v)} canGrant={canGrant} /> : null}
      {self && manageRoles && !isSystemAdmin(user) ? <div className="t-hint" style={{ fontSize: 12 }}>Não pode alterar os seus próprios perfis.</div> : null}
      {error ? <div style={{ background: "var(--error)", color: "var(--on-error)", padding: 10, borderRadius: "var(--r-md)", fontSize: 13 }} data-testid="member-form-error">{error}</div> : null}
      <Button loading={save.isPending} onClick={() => { setError(""); save.mutate(); }} data-testid="member-save">{isNew ? "CRIAR MEMBRO" : "GUARDAR"}</Button>
    </Sheet>
  );
}

function MemberRow({ m, onEdit, onDelete, canEdit, canDelete }) {
  return (
    <div className="card" style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, opacity: m.active === false ? 0.55 : 1 }} data-testid={`member-row-${m.id}`}>
      <div className="avatar" style={{ width: 42, height: 42, fontSize: 14 }}>
        {coverUri(m.photo_url) ? <img src={coverUri(m.photo_url)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(m.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--f-medium)", fontSize: 15 }}>{m.name}</div>
        <div className="t-hint">{m.email}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }} data-testid={`member-roles-${m.id}`}>
          {(m.roles || []).map((r) => <span key={r.id} className={`pill ${r.system_key === "admin" ? "todo" : ""}`} style={{ fontSize: 10 }}>{r.name.toUpperCase()}</span>)}
        </div>
      </div>
      {m.is_singer === false ? <span className="pill" data-testid={`member-not-singer-${m.id}`}>NÃO CANTA</span> : m.voice ? <span className="pill doing">{m.voice.toUpperCase()}</span> : null}
      {canEdit ? <button className="icon-btn" onClick={() => onEdit(m)} data-testid={`member-edit-${m.id}`}><Icon name="pencil" size={16} /></button> : null}
      {canDelete ? <button className="icon-btn" onClick={() => onDelete(m)} data-testid={`member-delete-${m.id}`}><Icon name="trash" size={16} color="var(--muted)" /></button> : null}
    </div>
  );
}

export default function AdminMembers() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(null);
  const q = useQuery({ queryKey: ["admin-members"], queryFn: () => api("/admin/members") });
  const rolesQ = useQuery({ queryKey: ["admin-roles"], queryFn: () => api("/admin/roles"), enabled: can(user, "roles.manage") });
  const members = q.data || [];
  const manage = can(user, "members.manage");
  const editable = manage || can(user, "roles.manage");
  const del = useMutation({
    mutationFn: (id) => api(`/admin/members/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-members"] }),
    onError: (e) => window.alert(e.message),
  });
  const [notice, setNotice] = useState(null);
  const saved = (n) => { qc.invalidateQueries({ queryKey: ["admin-members"] }); qc.invalidateQueries({ queryKey: ["admin-roles"] }); refreshUser(); setEditing(null); setNotice(n || null); };

  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="icon-btn" onClick={() => navigate(-1)}><Icon name="chevron-back" size={22} /></button>
        <h1 className="t-h1" style={{ flex: 1 }}>Membros</h1>
        {manage ? <Button className="auto sm" icon="add" onClick={() => setEditing({})} data-testid="member-new-button">NOVO MEMBRO</Button> : null}
      </div>
      {q.isLoading ? <Spinner /> : q.isError ? <EmptyState icon="alert-circle-outline" text={q.error.message} /> : (
        <div className="page ulc-fade" style={{ maxWidth: 900 }}>
          {notice ? (
            <div role="status" data-testid="member-notice" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, padding: 12, borderRadius: "var(--r-md)", background: notice.ok ? "var(--success)" : "var(--error)", color: notice.ok ? "var(--on-success)" : "var(--on-error)", fontSize: 14 }}>
              <Icon name={notice.ok ? "mail" : "warning"} size={16} /> <span style={{ flex: 1 }}>{notice.text}</span>
              <button className="icon-btn" aria-label="Fechar aviso" onClick={() => setNotice(null)} data-testid="member-notice-close"><Icon name="close" size={14} /></button>
            </div>
          ) : null}
          {!members.length ? <EmptyState icon="people" text="Ainda não há membros." /> : null}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {members.map((m) => (
              <MemberRow key={m.id} m={m} canEdit={editable} canDelete={manage && m.id !== user?.id && (isSystemAdmin(user) || !(m.roles || []).some((r) => r.system_key === "admin"))} onEdit={setEditing} onDelete={(x) => window.confirm(`Remover ${x.name}?`) && del.mutate(x.id)} />
            ))}
          </div>
        </div>
      )}
      {editing ? <MemberForm editing={editing} roles={rolesQ.data || []} onClose={() => setEditing(null)} onSaved={saved} /> : null}
    </>
  );
}
